import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { loadGoogleMapsLibrary } from "../works/useGooglePlacesSuggestions";
import { routeShape } from "./routeLegs";
import {
  ROUTE_LINE_COLOR,
  ROUTE_STOP_COLOR,
  routeMapFingerprint,
} from "../../utils/routeMapPoints";
import type { RouteMapStop } from "../../utils/routeMapPoints";

/**
 * ── A container's run, drawn ─────────────────────────────────────────────────
 *
 * The stops of one container in the order it makes them, joined by the road
 * between them, numbered to match the stop cards that sit under it. It reads
 * the same located stops the server measures the mileage over — the same stops,
 * under the same rules, so the map can never show a run the badge was not
 * counting.
 *
 * The *distance* is a different matter. Drawing the run is how the browser
 * comes to know the road distance, which the server cannot work out without a
 * Maps key of its own — so the total goes back to the caller through
 * `onMeasured`, and the card decides which figure to show rather than printing
 * two.
 *
 * Read-only by design, and cheap by design. One map instance per mounted
 * component and one Directions answer per distinct route (cached in
 * `routeLegs`), because the planning board can hold hundreds of containers and
 * a map per card is a bill, not a feature — there the map opens on request.
 *
 * Everything degrades rather than disappears: no browser key renders a note,
 * Directions refusing renders the straight-line estimate with the line dashed
 * and `onSource` told, and a run with one located stop renders that stop.
 */

const API_KEY = import.meta.env.VITE_PUBLIC_GOOGLE_MAPS_API_KEY || "";

/** The teardrop pin a stop wears, with its route number inside it. */
const pinSvg = (color: string, index: number) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">
     <path d="M15 39C15 39 28 23.8 28 14.5A13 13 0 1 0 2 14.5C2 23.8 15 39 15 39Z"
           fill="${color}" stroke="#FFFFFF" stroke-width="2.5"/>
     <text x="15" y="19.5" text-anchor="middle" font-family="Helvetica,Arial,sans-serif"
           font-size="13" font-weight="700" fill="#FFFFFF">${index}</text>
   </svg>`;

/**
 * Every leg's distance label, laid out together — for the same reason the pins
 * are (see `StopPins`), and it bites harder here than anyone expects.
 *
 * A container collected from a terminal and returned to it drives out and back
 * along the same road. The two legs are then the same road reversed: the same
 * length to the metre, and — this is the part that hides a label — the same
 * midpoint. Two chips reading "7232.0 km" land on the identical pixel, and the
 * map shows one. That is not an exotic route; a terminal pickup and a terminal
 * drop-off at the same terminal is the ordinary shape of the work.
 *
 * So the chips are de-collided in screen pixels on every `draw`, exactly as the
 * pins are, and stacked vertically rather than fanned: a pill is wide and short,
 * and wide-and-short things stack.
 */
interface LegChip {
  position: google.maps.LatLng;
  text: string;
}

type LegChipsCtor = new (chips: LegChip[]) => google.maps.OverlayView;

let LegChips: LegChipsCtor | null = null;

const legChipsClass = (maps: typeof google.maps): LegChipsCtor => {
  if (LegChips) return LegChips;

  /** A pill is about 90px by 18px. Overlap is judged as the box it is. */
  const NEAR_X = 94;
  const NEAR_Y = 24;

  /** Clear of each other, with a hairline of map showing between. */
  const STACK_PX = 24;

  LegChips = class Chips extends maps.OverlayView {
    private root = document.createElement("div");
    private nodes: HTMLElement[] = [];

    constructor(private chips: LegChip[]) {
      super();
    }

    onAdd() {
      this.root.style.cssText = "position:absolute;left:0;top:0;width:0;height:0";

      this.chips.forEach((chip) => {
        const node = document.createElement("div");
        node.textContent = chip.text;
        node.style.cssText = [
          "position:absolute",
          // Labels never swallow a drag — the map under them stays draggable.
          "pointer-events:none",
          "white-space:nowrap",
          "border-radius:9999px",
          "font-family:inherit",
          "box-shadow:0 1px 3px rgba(11,15,14,0.22)",
          "transform:translate(-50%,-50%)",
          "background:#FFFFFF;color:#586762;font-size:10.5px;font-weight:600",
          "padding:2px 7px",
        ].join(";");
        this.root.appendChild(node);
        this.nodes.push(node);
      });

      this.getPanes()?.floatPane.appendChild(this.root);
    }

    draw() {
      const projection = this.getProjection();
      if (!projection) return;

      const points = this.chips.map((chip) =>
        projection.fromLatLngToDivPixel(chip.position),
      );

      // Which chips are sitting on which, at *this* zoom — as boxes, not as
      // points, because a pill is five times wider than it is tall.
      const groups: Array<{ x: number; y: number; members: number[] }> = [];
      points.forEach((point, i) => {
        if (!point) return;
        const shared = groups.find(
          (group) =>
            Math.abs(group.x - point.x) < NEAR_X &&
            Math.abs(group.y - point.y) < NEAR_Y,
        );
        if (shared) shared.members.push(i);
        else groups.push({ x: point.x, y: point.y, members: [i] });
      });

      for (const group of groups) {
        const count = group.members.length;

        group.members.forEach((i, position) => {
          const point = points[i];
          if (!point) return;

          // Centred on the point they share, so the stack reads as one thing
          // happening in one place rather than as labels drifting downwards.
          const dy =
            count === 1 ? 0 : (position - (count - 1) / 2) * STACK_PX;

          this.nodes[i].style.left = `${point.x}px`;
          this.nodes[i].style.top = `${point.y + dy}px`;
        });
      }
    }

    onRemove() {
      this.root.remove();
    }
  };

  return LegChips;
};

interface StopPin {
  stop: RouteMapStop;
  /** What it is, for the tooltip — the role, and the place when there is one. */
  title: string;
  /** What is drawn beside the pin. Empty draws no label at all. */
  label: string;
}

type StopPinsCtor = new (
  pins: StopPin[],
  options: { compact: boolean },
) => google.maps.OverlayView;

let StopPins: StopPinsCtor | null = null;

/**
 * Every stop pin, laid out together — because none of them can be placed
 * without knowing where the others landed.
 *
 * ── Why one overlay and not one marker each ──────────────────────────────────
 *
 * A container collected from a terminal and returned to it has two stops on one
 * coordinate, and two markers on one coordinate is one marker: the reader sees
 * a two-stop run where there are three, and no amount of zooming separates
 * points that are genuinely identical.
 *
 * Nudging the coordinates apart does not fix it either, which is the lesson
 * that produced this class. A fixed nudge is a *distance on the ground*: fifteen
 * metres shows as a clear gap in a city street view and as nothing at all —
 * literally zero pixels — on a map showing half of Asia. The gap has to be a
 * distance on the *screen*, and a distance on the screen changes meaning every
 * time the zoom does.
 *
 * So the separation is computed in pixels, here, on every `draw` — and Google
 * calls `draw` on every zoom and every pan. Pins that project within
 * `COLLISION_PX` of each other are fanned onto a circle around the point they
 * share, each one keeping a thin leader back to where it actually is. Zoom in
 * far enough that two stops are genuinely apart and the fan stops happening by
 * itself, because they no longer collide.
 */
const stopPinsClass = (maps: typeof google.maps): StopPinsCtor => {
  if (StopPins) return StopPins;

  const SVG_NS = "http://www.w3.org/2000/svg";

  /** Pins whose points land closer than this share a fan. Pin width is 30. */
  const COLLISION_PX = 30;

  /** Adjacent pins on a fan sit at least this far apart, centre to centre. */
  const SPACING_PX = 34;

  StopPins = class Pins extends maps.OverlayView {
    private root = document.createElement("div");
    private leaders = document.createElementNS(SVG_NS, "svg");
    private nodes: HTMLElement[] = [];
    private lines: SVGLineElement[] = [];
    private points: google.maps.LatLng[] = [];

    constructor(
      private pins: StopPin[],
      private options: { compact: boolean },
    ) {
      super();
    }

    onAdd() {
      this.root.style.cssText = "position:absolute;left:0;top:0;width:0;height:0";
      this.leaders.setAttribute(
        "style",
        "position:absolute;left:0;top:0;width:1px;height:1px;overflow:visible;pointer-events:none",
      );
      this.root.appendChild(this.leaders);

      this.pins.forEach(({ stop, title, label: text }, i) => {
        this.points.push(new maps.LatLng({ lat: stop.lat, lng: stop.lng }));

        // Drawn before the pins so a leader always passes beneath them.
        const line = document.createElementNS(SVG_NS, "line");
        line.setAttribute("stroke", "#0B0F0E");
        line.setAttribute("stroke-width", "1.5");
        line.setAttribute("stroke-opacity", "0.45");
        line.style.display = "none";
        this.leaders.appendChild(line);
        this.lines.push(line);

        const node = document.createElement("div");
        // The pin's tip is its anchor, so it hangs above the point it marks.
        node.style.cssText = `position:absolute;width:30px;height:40px;transform:translate(-50%,-100%);z-index:${10 + i}`;
        node.title = `${stop.index} · ${title}`;
        node.innerHTML = pinSvg(
          ROUTE_STOP_COLOR[stop.role] ?? ROUTE_STOP_COLOR.custom,
          stop.index,
        );

        if (text) {
          const label = document.createElement("span");
          label.textContent = `${stop.index} · ${text}`;
          label.style.cssText = [
            "position:absolute;left:32px;top:13px;transform:translateY(-50%)",
            "pointer-events:none;white-space:nowrap;border-radius:9999px",
            "font-family:inherit;font-weight:700",
            this.options.compact
              ? "font-size:10px;padding:2px 6px"
              : "font-size:11.5px;padding:3px 9px",
            "background:#FFFFFF;color:#0B0F0E",
            "box-shadow:0 1px 3px rgba(11,15,14,0.22)",
          ].join(";");
          node.appendChild(label);
        }

        this.root.appendChild(node);
        this.nodes.push(node);
      });

      this.getPanes()?.floatPane.appendChild(this.root);
    }

    draw() {
      const projection = this.getProjection();
      if (!projection) return;

      const anchors = this.points.map((point) =>
        projection.fromLatLngToDivPixel(point),
      );

      // Who is sitting on whom, at *this* zoom. First one in owns the point,
      // so a fan does not drift as the others join it.
      const groups: Array<{ x: number; y: number; members: number[] }> = [];
      anchors.forEach((point, i) => {
        if (!point) return;
        const shared = groups.find(
          (group) => Math.hypot(group.x - point.x, group.y - point.y) < COLLISION_PX,
        );
        if (shared) shared.members.push(i);
        else groups.push({ x: point.x, y: point.y, members: [i] });
      });

      for (const group of groups) {
        const count = group.members.length;

        if (count === 1) {
          const i = group.members[0];
          this.place(i, anchors[i], 0, 0);
          continue;
        }

        // Big enough that neighbours on the circle clear each other, and no
        // bigger — a fan that flings pins across the map stops reading as
        // "these are all here".
        const radius = Math.max(26, SPACING_PX / 2 / Math.sin(Math.PI / count));

        group.members.forEach((i, position) => {
          // From the top, clockwise: with two stops that is one above the
          // point and one below, which is the common case and the clearest.
          const angle = -Math.PI / 2 + (2 * Math.PI * position) / count;
          this.place(
            i,
            anchors[i],
            Math.cos(angle) * radius,
            Math.sin(angle) * radius,
          );
        });
      }
    }

    /** One pin, at its point plus however far it had to move to be seen. */
    private place(
      i: number,
      anchor: google.maps.Point | null,
      dx: number,
      dy: number,
    ) {
      if (!anchor) return;

      const node = this.nodes[i];
      node.style.left = `${anchor.x + dx}px`;
      node.style.top = `${anchor.y + dy}px`;

      const line = this.lines[i];
      if (!dx && !dy) {
        line.style.display = "none";
        return;
      }

      // A moved pin says where it really belongs, or it is just a pin in the
      // wrong place.
      line.style.display = "";
      line.setAttribute("x1", String(anchor.x + dx));
      line.setAttribute("y1", String(anchor.y + dy));
      line.setAttribute("x2", String(anchor.x));
      line.setAttribute("y2", String(anchor.y));
    }

    onRemove() {
      this.root.remove();
    }
  };

  return StopPins;
};

/** Close stops must not fit-bounds their way down to a single street. */
const MAX_FIT_ZOOM = 15;

/** How far the drawn run is, and what kind of measurement that is. */
export interface RouteMeasure {
  source: "road" | "straight";
  km: number;
}

interface RouteMapProps {
  /** The located stops of the run, in order. */
  stops: RouteMapStop[];
  /** What each stop is called, for its tooltip — keyed by stop id. */
  titles: Record<string, string>;
  /**
   * What to draw beside each pin, keyed by stop id — the short place name.
   *
   * Separate from `titles` because the map and the list under it answer
   * different questions: the pin says *where* ("Peshawar") and the list says
   * *what* ("Loading site"). Saying the same thing twice would waste the one
   * label a pin can carry.
   *
   * Omitted, a pin falls back to its title — which is what the container's own
   * page wants, having room for it — and draws nothing when compact.
   */
  labels?: Record<string, string>;
  /** Height of the canvas; the caller owns it because the board's is smaller. */
  className?: string;
  /**
   * Drop the stop-name labels and thin the line.
   *
   * For the board's preview, where the map is a quarter of the size and the
   * names are already listed beside it.
   */
  compact?: boolean;
  /**
   * Draw the stops and nothing else — no road between them.
   *
   * For the planning board, where a card expands to show *where this container
   * goes*: three points a dispatcher reads in a second. The line joining them
   * is the wrong answer to that question at that size, and drawing it costs a
   * Directions request per card opened — so `pinsOnly` skips the lookup
   * entirely rather than fetching a route it then declines to draw.
   */
  pinsOnly?: boolean;
  /**
   * What the drawn run actually measures — called exactly once per route.
   *
   * The map is the only part of the page that knows the *road* distance when
   * the server has no Maps key of its own, and a card showing a straight-line
   * total beside a road line is a card contradicting itself — so the figure
   * goes back up to whoever is showing one. `km` is summed before the legs are
   * rounded for their labels.
   *
   * `null` means there will be no measurement from here: no key, no located
   * stops, only one of them, `pinsOnly`, or the map failed to load. It is
   * reported rather than left silent because the difference between "still
   * measuring" and "nothing to measure" is the difference between a caller
   * waiting for a better answer and a caller waiting forever.
   */
  onMeasured?: (measure: RouteMeasure | null) => void;
}

export function RouteMap({
  stops,
  titles,
  labels,
  className = "h-72",
  compact = false,
  pinsOnly = false,
  onMeasured,
}: RouteMapProps) {
  const { t } = useTranslation("containerOrders");
  const hostRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);

  // The effect redraws when the *route* changes, not when the array identity
  // does — a parent re-render must not cost a Directions request.
  const fingerprint = useMemo(() => routeMapFingerprint(stops), [stops]);

  // Held in a ref so a caller passing an inline arrow does not redraw the map.
  const onMeasuredRef = useRef(onMeasured);
  onMeasuredRef.current = onMeasured;

  const titlesRef = useRef(titles);
  titlesRef.current = titles;

  const labelsRef = useRef(labels);
  labelsRef.current = labels;

  useEffect(() => {
    if (!API_KEY || !stops.length) {
      onMeasuredRef.current?.(null);
      return;
    }

    let cancelled = false;
    // Markers, polylines and labels alike: everything drawn has to be taken
    // off the map by hand, or it outlives the div it was drawn on.
    const drawn: Array<{ setMap: (map: google.maps.Map | null) => void }> = [];
    const teardown: Array<() => void> = [];

    /** Put the whole run on screen, at a zoom that still reads as a journey. */
    const frame = (
      map: google.maps.Map,
      maps: typeof google.maps,
      bounds: google.maps.LatLngBounds,
    ) => {
      if (stops.length === 1) {
        map.setCenter({ lat: stops[0].lat, lng: stops[0].lng });
        map.setZoom(13);
        return;
      }

      // Room for the labels, which hang off their pins to the right, and for a
      // fan, which can lift a pin a further 26px clear of its point.
      const labelled = !compact || Boolean(labelsRef.current);
      map.fitBounds(
        bounds,
        labelled
          ? compact
            ? { top: 44, right: 76, bottom: 44, left: 20 }
            : { top: 72, right: 120, bottom: 64, left: 48 }
          : 48,
      );

      // Two points in one yard fit at street level, which shows the reader a
      // car park rather than a journey. Capped once the fit has settled —
      // `fitBounds` sets the zoom asynchronously.
      const settle = maps.event.addListenerOnce(map, "idle", () => {
        if ((map.getZoom() ?? 0) > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
      });
      teardown.push(() => settle.remove());
    };

    (async () => {
      try {
        // Imported explicitly rather than relying on what the bootstrap pulled
        // in: `importLibrary` is cached, so asking twice costs nothing. No
        // `marker` library — the pins are overlays of our own, because Google's
        // markers cannot be laid out against each other (see StopPins).
        await Promise.all(
          ["core", "maps", "routes"].map(loadGoogleMapsLibrary),
        );
        if (cancelled || !hostRef.current) return;

        const maps: typeof google.maps = window.google.maps;
        const map = new maps.Map(hostRef.current, {
          center: { lat: stops[0].lat, lng: stops[0].lng },
          zoom: 12,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          keyboardShortcuts: false,
          zoomControl: true,
          zoomControlOptions: { position: maps.ControlPosition.LEFT_TOP },
          // Inside a long read-only page a wheel must scroll the page, not the
          // map. In the board's preview the map is the whole dialog, so there
          // it takes the wheel.
          gestureHandling: compact ? "greedy" : "cooperative",
        });
        setReady(true);

        const bounds = new maps.LatLngBounds();

        stops.forEach((stop) => bounds.extend({ lat: stop.lat, lng: stop.lng }));

        // Every pin in one overlay: where each lands depends on where the
        // others landed, and that answer changes with the zoom — see StopPins.
        const pins = new (stopPinsClass(maps))(
          stops.map((stop) => {
            const title = titlesRef.current[stop.id] ?? "";
            const place = labelsRef.current?.[stop.id];

            return {
              stop,
              // The tooltip carries both, since it has the room the pin has not.
              title: place ? `${title} · ${place}` : title,
              label: place ?? (compact ? "" : title),
            };
          }),
          { compact },
        );
        pins.setMap(map);
        drawn.push(pins);

        if (pinsOnly) {
          frame(map, maps, bounds);
          // Nothing was measured, and nothing will be — see `onMeasured`.
          onMeasuredRef.current?.(null);
          return;
        }

        const shape = await routeShape(stops);
        if (cancelled) return;
        // A single-stop run is neither a road distance nor an estimate, so it
        // reports no measurement rather than a meaningless zero.
        onMeasuredRef.current?.(
          shape.legs.length
            ? { source: shape.source, km: shape.totalKm }
            : null,
        );

        const dashed = shape.source === "straight";

        const legChips: LegChip[] = [];

        shape.legs.forEach((leg, index) => {
          if (leg.path.length < 2) return;
          leg.path.forEach((point) => bounds.extend(point));

          // A white stroke under the route — the line has to stay readable
          // where it runs along a motorway drawn in a colour close to it.
          if (!dashed) {
            drawn.push(
              new maps.Polyline({
                map,
                path: leg.path,
                strokeColor: "#FFFFFF",
                strokeOpacity: 0.9,
                strokeWeight: compact ? 6 : 9,
                zIndex: 1,
              }),
            );
          }

          drawn.push(
            new maps.Polyline({
              map,
              path: leg.path,
              strokeColor: ROUTE_LINE_COLOR,
              // Dashed says "estimated" without a legend: the straight-line
              // fallback must never be mistaken for a road.
              strokeOpacity: dashed ? 0 : 0.95,
              strokeWeight: compact ? 3.5 : 5,
              zIndex: 2,
              ...(dashed
                ? {
                    icons: [
                      {
                        icon: {
                          path: "M 0,-1 0,1",
                          strokeColor: ROUTE_LINE_COLOR,
                          strokeOpacity: 0.95,
                          strokeWeight: compact ? 3 : 4,
                          scale: 3,
                        },
                        offset: "0",
                        repeat: "14px",
                      },
                    ],
                  }
                : {}),
            }),
          );

          const distance = `${dashed ? "≈ " : ""}${leg.km.toFixed(1)} km`;

          // Which hop this is, in the numbers the pins wear. Out-and-back runs
          // put two equal distances on one point, and "7232.0 km" twice over
          // says nothing about which line is which — "1→2" does.
          const from = stops[index]?.index;
          const to = stops[index + 1]?.index;

          legChips.push({
            position: new maps.LatLng(leg.path[Math.floor(leg.path.length / 2)]),
            text: from && to ? `${from}→${to} · ${distance}` : distance,
          });
        });

        if (legChips.length) {
          const chips = new (legChipsClass(maps))(legChips);
          chips.setMap(map);
          drawn.push(chips);
        }

        if (cancelled) return;
        frame(map, maps, bounds);
      } catch (error) {
        console.error("Route map:", (error as Error)?.message);
        if (cancelled) return;
        setFailed(true);
        // A caller holding its figure back for a better one has to be let go.
        onMeasuredRef.current?.(null);
      }
    })();

    return () => {
      cancelled = true;
      drawn.forEach((item) => item.setMap(null));
      teardown.forEach((off) => off());
    };
    // `stops` is deliberately not a dependency: the route is what matters, and
    // `fingerprint` is the route. A parent re-render handing over an equal but
    // new array must not cost a map instance and a Directions request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint, compact, pinsOnly]);

  if (!stops.length) {
    return (
      <div
        className={`flex items-center justify-center rounded-md border border-dashed border-[rgba(11,15,14,0.14)] bg-bone/60 px-4 text-center text-[12px] text-mist ${className}`}
      >
        {t("lineRoute.mapNoLocations")}
      </div>
    );
  }

  if (!API_KEY || failed) {
    return (
      <div
        className={`flex items-center justify-center rounded-md border border-dashed border-[rgba(11,15,14,0.14)] bg-bone/60 px-4 text-center text-[12px] text-mist ${className}`}
      >
        {t("lineRoute.mapUnavailable")}
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-md border border-[rgba(11,15,14,0.08)] bg-bone ${className}`}
    >
      <div ref={hostRef} className="h-full w-full" />
      {!ready && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[12px] text-mist">
          {t("lineRoute.mapLoading")}
        </div>
      )}
    </div>
  );
}
